import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Briefcase, ClipboardList, CalendarCheck, Truck, FileText, Flame, LayoutDashboard } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { openDrawing } from "../../drawings/openDrawing";
import StatusPill from "../components/StatusPill";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-slate-200 bg-white p-4 flex items-start gap-3"
    >
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
      <div className="flex-1">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
        <div className="stat-num mt-1 text-2xl font-semibold text-slate-900 leading-tight">{value}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  const s = stats || {};
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard testid="stat-active-projects" icon={Briefcase} label="Active Jobs" value={s.active_projects ?? 0} accent="bg-blue-50 text-blue-600" />
        <StatCard testid="stat-pending-mobs" icon={ClipboardList} label="Pending Mobs" value={s.pending_mobilizations ?? 0} accent="bg-sky-50 text-sky-600" />
        <StatCard testid="stat-scheduled-mobs" icon={CalendarCheck} label="Scheduled" value={s.scheduled_mobilizations ?? 0} accent="bg-indigo-50 text-indigo-600" />
        <StatCard testid="stat-dispatched-crews" icon={Truck} label="Dispatched" value={s.dispatched_crews ?? 0} accent="bg-teal-50 text-teal-600" />
        <StatCard testid="stat-open-lineouts" icon={FileText} label="Open Lineouts" value={s.open_lineouts ?? 0} accent="bg-violet-50 text-violet-600" />
        <StatCard testid="stat-needs-review" icon={Flame} label="Priority" value={(s.priority_actions || []).length} accent="bg-orange-50 text-orange-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
        <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <header className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <Flame className="w-4 h-4 text-orange-600" strokeWidth={1.75} />
            <h2 className="text-sm font-semibold text-gray-900">Priority Actions</h2>
            <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 ml-1">
              {(s.priority_actions || []).length}
            </span>
          </header>
          <div className="divide-y divide-gray-100">
            {(s.priority_actions || []).length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-500">No priority items in next 3 days.</div>
            ) : (
              (s.priority_actions || []).map((m) => (
                <div key={m.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{m.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{m.building} · Floor {m.floor || "—"} · {m.scope_summary}</div>
                  </div>
                  {m.project_id && (
                    <button type="button" data-testid={`priority-plan-${m.id}`}
                            onClick={() => openDrawing(navigate, { project: m.project_id, from: "dashboard" })}
                            title="Open this job's drawings"
                            className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 hover:bg-blue-100">
                      plan
                    </button>
                  )}
                  <StatusPill value={m.status} />
                </div>
              ))
            )}
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <header className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-blue-600" strokeWidth={1.75} />
            <h2 className="text-sm font-semibold text-gray-900">Recent Jobs</h2>
          </header>
          <div className="divide-y divide-gray-100">
            {(s.recent_projects || []).length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-500">
                No projects yet. <Link to="/projects" className="text-blue-600 hover:underline">Create one</Link>.
              </div>
            ) : (
              (s.recent_projects || []).map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  data-testid={`recent-project-${p.id}`}
                  className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{p.project_name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{p.job_number} · {p.city}, {p.state}</div>
                  </div>
                  <StatusPill value={p.status} />
                </Link>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
